import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import useApi from 'api/api';
import appConfig from 'appConfig';
import { OrderMessage } from 'types/order';
import { sanitizeHtmlForDisplay } from 'utils/htmlSanitizer';

dayjs.extend(utc);
dayjs.extend(timezone);

interface OrderMessagesDialogProps {
  open: boolean;
  onClose: () => void;
  orderSeqId: string | null;
  processHtmlForDarkMode?: (html: string) => string;
}

export default function OrderMessagesDialog({
  open,
  onClose,
  orderSeqId,
  processHtmlForDarkMode
}: OrderMessagesDialogProps) {
  const theme = useTheme();
  const { get } = useApi();
  const [orderMessages, setOrderMessages] = useState<OrderMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  // Process HTML: first sanitize for security, then apply dark mode theme adjustments
  const processHtml = (html: string) => {
    // Step 1: Sanitize HTML (encodes invalid tags, removes dangerous content)
    const sanitized = sanitizeHtmlForDisplay(html);
    
    // Step 2: Apply dark mode theme adjustments if provided
    if (processHtmlForDarkMode) {
      return processHtmlForDarkMode(sanitized);
    }
    
    return sanitized;
  };

  // Fetch messages when dialog opens
  useEffect(() => {
    if (open && orderSeqId) {
      fetchMessages();
    } else {
      // Reset state when dialog closes
      setOrderMessages([]);
      setMessagesError(null);
    }
  }, [open, orderSeqId]);

  const fetchMessages = async () => {
    if (!orderSeqId) return;

    setLoadingMessages(true);
    setMessagesError(null);
    setOrderMessages([]);

    try {
      const response = await get<OrderMessage[]>(
        `${appConfig.api.orderApiUrl}/orders/${orderSeqId}/messages`
      );
      if (response?.data) {
        setOrderMessages(response.data);
      } else {
        setMessagesError('Failed to load messages');
      }
    } catch (error) {
      console.error('Error fetching order messages:', error);
      setMessagesError('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="order-messages-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="order-messages-dialog-title">
        Order Messages - {orderSeqId}
      </DialogTitle>
      <DialogContent>
        {loadingMessages ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: '200px' }}
          >
            <CircularProgress />
          </Box>
        ) : messagesError ? (
          <Typography color="error">{messagesError}</Typography>
        ) : orderMessages.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No messages available.
          </Typography>
        ) : (
          <Box
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              overflowX: 'clip',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '10px',
              padding: '0px',
              backgroundColor: theme.palette.background.paper
            }}
          >
            {orderMessages
              .slice()
              .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
              .map((message, index) => (
                <Box
                  key={message.executionOrderMessageId || index}
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    padding: '8px',
                    backgroundColor: theme.palette.background.paper,
                    height: 'fit-content',
                    minHeight: 'fit-content',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.2,
                      margin: 0,
                      padding: 0
                    }}
                    dangerouslySetInnerHTML={{
                      __html: processHtml(message.msgTextHtml)
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    align="right"
                    sx={{
                      display: { xs: 'inline', md: 'block' },
                      float: { xs: 'none', md: 'right' },
                      top: { xs: '5px', md: '-13px' },
                      position: 'relative',
                      border: `solid 1px ${theme.palette.divider}`,
                      paddingRight: '5px',
                      paddingLeft: '5px',
                      left: { xs: '0px', md: '11px' },
                      margin: 0,
                      lineHeight: 1.2
                    }}
                  >
                    {`${message.updatedBy} : ${dayjs
                      .utc(message.dateCreated)
                      .tz(dayjs.tz.guess())
                      .format('MMM D, YYYY, HH:mm:ss')}`}
                  </Typography>
                </Box>
              ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

